import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';

/**
 * JobCancellationModal - Modal for cancelling a job with review and blacklist options
 * 
 * Props:
 * - show: boolean - whether modal is visible
 * - onHide: function - called when modal is closed
 * - onSubmit: function - called with cancellation data { cancellationReason, starRating, reviewText, isBlacklisted, blacklistReason }
 * - job: object - the job being cancelled (for customer info)
 * - isSubmitting: boolean - whether submission is in progress
 */
export default function JobCancellationModal({ show, onHide, onSubmit, job, isSubmitting = false }) {
  const [cancellationReason, setCancellationReason] = useState('');
  const [starRating, setStarRating] = useState(5); // Default 5 stars
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate cancellation reason
    if (!cancellationReason.trim()) {
      alert('Please select a reason for cancelling this job');
      return;
    }
    
    // Validate blacklist reason if blacklist is checked
    if (isBlacklisted && !blacklistReason.trim()) {
      alert('Please provide a reason for blacklisting this customer');
      return;
    }
    
    onSubmit({
      cancellationReason: cancellationReason.trim(),
      starRating,
      reviewText: reviewText.trim(),
      isBlacklisted,
      blacklistReason: isBlacklisted ? blacklistReason.trim() : ''
    });
  };

  const handleClose = () => {
    // Reset form
    setCancellationReason('');
    setStarRating(5);
    setReviewText('');
    setIsBlacklisted(false);
    setBlacklistReason('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#dc3545', color: 'white' }}>
        <Modal.Title>❌ Cancel Job</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {job && (
          <div className="mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <small>
              <strong>{job.customerName}</strong> • {job.customerPhone}<br/>
              {job.from} → {job.to}
            </small>
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Cancellation Reason Section */}
          <div className="mb-4">
            <h6 className="mb-3">📋 Cancellation Reason *</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="within15min"
                    value="within15min"
                    checked={cancellationReason === 'within15min'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="within15min">
                    Within 15 minutes
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="within30min"
                    value="within30min"
                    checked={cancellationReason === 'within30min'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="within30min">
                    Within 30 minutes
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="within1hr"
                    value="within1hr"
                    checked={cancellationReason === 'within1hr'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="within1hr">
                    Within 1 hour
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="noShow"
                    value="noShow"
                    checked={cancellationReason === 'noShow'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="noShow">
                    No Show
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="abusive"
                    value="abusive"
                    checked={cancellationReason === 'abusive'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="abusive">
                    Abusive
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="inputError"
                    value="inputError"
                    checked={cancellationReason === 'inputError'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="inputError">
                    Input Error
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="trainBusCancellation"
                    value="trainBusCancellation"
                    checked={cancellationReason === 'trainBusCancellation'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="trainBusCancellation">
                    Train/Bus Cancellation
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="duplicate"
                    value="duplicate"
                    checked={cancellationReason === 'duplicate'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="duplicate">
                    Duplicate
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="noLongerRequired"
                    value="noLongerRequired"
                    checked={cancellationReason === 'noLongerRequired'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="noLongerRequired">
                    No Longer Required
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="test"
                    value="test"
                    checked={cancellationReason === 'test'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="test">
                    Test
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cancellationReason"
                    id="other"
                    value="other"
                    checked={cancellationReason === 'other'}
                    onChange={(e) => setCancellationReason(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="other">
                    Other
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Star Rating Section */}
          <div className="mb-3">
            <h6 className="mb-2">⭐ Customer Review</h6>
            <div className="d-flex align-items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={32}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                  color={(hoverRating || starRating) >= star ? '#ffc107' : '#e4e5e9'}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setStarRating(star)}
                />
              ))}
              <span className="ms-1" style={{ fontWeight: 'bold' }}>
                {starRating}/5
              </span>
            </div>
          </div>

          {/* Optional Review Text */}
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={2}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Review notes (optional)"
              maxLength={500}
              size="sm"
            />
          </Form.Group>

          {/* Blacklist Section */}
          <div className="mb-2 p-2" style={{ 
            backgroundColor: isBlacklisted ? '#fff3cd' : '#f8f9fa', 
            borderRadius: '6px',
            border: isBlacklisted ? '2px solid #ffc107' : '1px solid #dee2e6'
          }}>
            <Form.Check
              type="checkbox"
              id="blacklistCheckbox"
              checked={isBlacklisted}
              onChange={(e) => setIsBlacklisted(e.target.checked)}
              label={<span style={{ fontWeight: 'bold' }}>⚠️ Blacklist Customer</span>}
            />

            {isBlacklisted && (
              <Form.Control
                className="mt-2"
                as="textarea"
                rows={2}
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="Reason for blacklisting *"
                maxLength={1000}
                required={isBlacklisted}
                size="sm"
                style={{ borderColor: isBlacklisted && !blacklistReason ? '#dc3545' : undefined }}
              />
            )}
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button 
          variant="secondary" 
          size="sm"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          variant="danger" 
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || !cancellationReason.trim() || (isBlacklisted && !blacklistReason.trim())}
        >
          {isSubmitting ? (
            <><span className="spinner-border spinner-border-sm me-1" />Cancelling...</>
          ) : (
            <>❌ Cancel Job</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
